import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { uploadSingleFile, bulkUpload, uploadSingleFileViaCompanion, uploadImageBatchViaCompanion, syncCompanionSession, parseBulkUploadDuplicates } from "../services/uploadService";
import {
  initializeChunkedUpload,
  uploadChunk,
  finalizeChunkedUpload,
  hashFileSha256,
} from "../services/chunkedUploadService";
import {
  getVideoMeta,
  compressVideo,
  loadFFmpeg,
  COMPRESSION_DISABLED,
  checkCompanion,
  INSUFFICIENT_COMPANION_DISK_SPACE,
} from "../services/videoCompressionService";
import { needsCompression, compressImage } from "../services/imageCompressionService";
import { getWatermark } from "../services/watermarkService";
import { resolveMetaDateTime } from "../utils/mediaDateTime";
import { toast } from "react-toastify";

/** Files larger than this use chunked upload to bypass Cloudflare limits */
const CHUNKED_UPLOAD_THRESHOLD = 95 * 1024 * 1024; // 95 MB
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per chunk

const UploadContext = createContext(null);

/**
 * Global upload state manager.
 * The upload pipeline runs HERE so it survives modal minimize/unmount.
 */
export const UploadProvider = ({ children }) => {
  const [sessions, setSessions] = useState({});
  const sessionsRef = useRef({}); // mirrors state synchronously for reads inside async pipelines
  const abortRefs = useRef({}); // { sessionId: boolean }
  const [uploadVersion, setUploadVersion] = useState(0); // increments on every session completion

  // Helper: update both state and ref atomically
  const updateSessions = useCallback((updater) => {
    setSessions((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      sessionsRef.current = next;
      return next;
    });
  }, []);

  // Pre-warm FFmpeg WASM only when companion is unavailable.
  // This keeps companion as the primary path and avoids noisy worker errors
  // (e.g., MT-core atob load failures) when WASM is not needed.
  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      if (COMPRESSION_DISABLED) return;

      const companion = await checkCompanion();
      if (cancelled || companion) return;

      loadFFmpeg().catch(() => {});
    })();

    return () => { cancelled = true; };
  }, []);

  // Sync PP auth context into Studio so Studio can fetch upload sources without manual token input.
  React.useEffect(() => {
    let disposed = false;
    (async () => {
      if (disposed) return;
      await syncCompanionSession();
    })();

    const timer = setInterval(() => {
      if (disposed) return;
      syncCompanionSession().catch(() => {});
    }, 30000);

    return () => {
      disposed = true;
      clearInterval(timer);
    };
  }, []);

  // Warn before browser close/refresh when uploads are active
  React.useEffect(() => {
    const handler = (e) => {
      const active = Object.values(sessions).some(
        (s) => s.started && s.fileItems?.some((f) => ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status))
      );
      if (active) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [sessions]);

  const patchSession = useCallback((sessionId, patch) => {
    updateSessions((prev) => {
      if (!prev[sessionId]) return prev;
      return { ...prev, [sessionId]: { ...prev[sessionId], ...patch } };
    });
  }, [updateSessions]);

  const patchFileItem = useCallback((sessionId, fileId, patch) => {
    updateSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;
      const fileItems = session.fileItems.map((item) =>
        item.id === fileId ? { ...item, ...patch } : item
      );
      return { ...prev, [sessionId]: { ...session, fileItems } };
    });
  }, [updateSessions]);

  const removeSession = useCallback((id) => {
    abortRefs.current[id] = true;
    updateSessions((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, [updateSessions]);

  /** Upload a single file (called within pipeline) — auto-picks chunked for large files */
  const uploadOneFile = useCallback(async (sessionId, item, uploadParams) => {
    const fileSize = item.processedFile.size;
    const isVideo = item.fileType?.startsWith("video/");
    const timerLabel = `[Upload] "${item.fileName}" (${(fileSize / 1e6).toFixed(2)} MB)`;
    console.time(timerLabel);
    const uploadStart = performance.now();
    patchFileItem(sessionId, item.id, { status: "uploading", uploadProgress: 0, eta: null, uploadMbps: 0, uploadPhase: "server" });

    try {
      const commonPayload = {
        file: item.processedFile,
        metaDateTime: item.metaDateTime,
        chatRoomId: uploadParams.chatRoomId,
        eventGalleryId: uploadParams.eventGalleryId,
        galleryAlbumId: uploadParams.galleryAlbumId,
        folderName: uploadParams.folderName,
        countryCode: uploadParams.countryCode,
        isCompressed: item.compressed || false,
        MaxCompressed: true,
        isWatermark: uploadParams.isWatermark || false,
        WMbyComanion: item.watermarkDoneByCompanion || false,
        forceCompress: false,
      };

      const applyProgress = (evt) => {
        const phase = evt.phase || "server";
        const pct = Math.round((evt.loaded * 100) / evt.total);
        let eta = null;
        let uploadMbps = Number(evt.avgMbps || evt.speedMbps || 0);
        if (evt.loaded > 0 && pct < 100) {
          const elapsedSec = (performance.now() - uploadStart) / 1000;
          const speed = evt.loaded / elapsedSec;
          const remaining = evt.total - evt.loaded;
          eta = Math.ceil(remaining / speed);
          if (!uploadMbps || !isFinite(uploadMbps)) {
            uploadMbps = (speed * 8) / 1_000_000;
          }
        }
        if (pct <= 1 && !navigator.onLine) {
          patchFileItem(sessionId, item.id, { status: "retrying", uploadProgress: 0, eta: null, uploadMbps: 0 });
        } else {
          patchFileItem(sessionId, item.id, {
            status: "uploading",
            uploadProgress: pct,
            eta,
            uploadMbps: Math.max(0, Number(uploadMbps || 0)),
            uploadPhase: phase,
          });
        }
      };

      // Reported by whichever path actually ran, then applied once at the bottom.
      let duplicateInfo = { duplicate: false, existingMediaId: null, existingUrl: null };

      // Files that are already uploaded to this event get skipped instead of re-uploaded
      // (server dedupes by exact byte content) -- this marks the item "done" (nothing
      // failed) but flagged so the UI can tell the user why.
      const applyDuplicateAndFinish = (dup) => {
        console.timeEnd(timerLabel);
        patchFileItem(sessionId, item.id, {
          status: "done",
          uploadProgress: 100,
          processedFile: null,
          originalFile: null,
          uploadPhase: "server",
          duplicate: true,
          existingMediaId: dup?.existingMediaId ?? null,
          existingUrl: dup?.existingUrl ?? null,
        });
        return "duplicate";
      };

      // Try companion upload first for ALL media (videos and images) so the
      // PSS activity window shows live upload jobs regardless of watermark status.
      const isImage = item.fileType?.startsWith("image/");
      if (isVideo || isImage) {
        try {
          const companionResult = await uploadSingleFileViaCompanion(commonPayload, applyProgress);
          if (companionResult?.duplicate) {
            return applyDuplicateAndFinish(companionResult);
          }
          console.timeEnd(timerLabel);
          patchFileItem(sessionId, item.id, { status: "done", uploadProgress: 100, processedFile: null, originalFile: null, duplicate: false });
          return "done";
        } catch (companionErr) {
          console.warn(`[Upload] Companion upload failed for "${item.fileName}". Falling back to browser upload.`, companionErr?.message || companionErr);
        }
      }

      // ── Large files → chunked upload (bypasses Cloudflare body-size limit) ──
      if (fileSize > CHUNKED_UPLOAD_THRESHOLD) {
        console.log(`[Upload] "${item.fileName}" is ${(fileSize / 1e6).toFixed(1)} MB → using chunked upload`);

        const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);
        const WMbyComanion = !!item.watermarkDoneByCompanion;

        console.log("[Upload] Chunked watermark flags", {
          fileName: item.fileName,
          isWatermark: !!uploadParams.isWatermark,
          WMbyComanion,
          compressed: !!item.compressed,
          originalSize: item.originalFile?.size ?? null,
          processedSize: fileSize,
        });

        // Hash before transferring anything -- lets Initialize reject a duplicate video
        // without the browser uploading a single byte of it.
        const contentHash = await hashFileSha256(item.processedFile);

        // Step 1: Initialize session
        let initRes;
        try {
          initRes = await initializeChunkedUpload({
            chatRoomId: uploadParams.chatRoomId,
            fileName: item.fileName,
            totalSize: fileSize,
            totalChunks,
            chunkSize: CHUNK_SIZE,
            contentType: item.processedFile.type || "application/octet-stream",
            contentHash,
            eventGalleryId: uploadParams.eventGalleryId,
            galleryAlbumId: uploadParams.galleryAlbumId,
            metaDateTime: item.metaDateTime,
            isWatermark: uploadParams.isWatermark || false,
            WMbyComanion,
            isCompressed: item.compressed || false,
            forceCompress: false,
          });
        } catch (err) {
          if (err.response?.status === 409) {
            const dup = err.response.data?.Result || {};
            return applyDuplicateAndFinish({ existingMediaId: dup.ExistingMediaId, existingUrl: dup.ExistingUrl });
          }
          throw err;
        }

        const chunkSessionId = initRes.data.Result.SessionId;

        // Step 2: Upload chunks sequentially
        for (let i = 0; i < totalChunks; i++) {
          if (abortRefs.current[sessionId]) throw new Error("Upload cancelled");

          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, fileSize);
          const chunkBlob = item.processedFile.slice(start, end);

          await uploadChunk({
            sessionId: chunkSessionId,
            chunkNumber: i,
            chunkData: chunkBlob,
            fileName: item.fileName,
          });

          const pct = Math.round(((i + 1) / totalChunks) * 100);
          let eta = null;
          if (pct < 100) {
            const elapsedSec = (performance.now() - uploadStart) / 1000;
            const speed = ((i + 1) * CHUNK_SIZE) / elapsedSec;
            const remaining = fileSize - (i + 1) * CHUNK_SIZE;
            eta = Math.ceil(remaining / speed);
          }
          const elapsedSec = Math.max(0.001, (performance.now() - uploadStart) / 1000);
          const uploadedBytes = Math.min(fileSize, (i + 1) * CHUNK_SIZE);
          const uploadMbps = (uploadedBytes * 8) / (elapsedSec * 1_000_000);
          patchFileItem(sessionId, item.id, {
            status: "uploading",
            uploadProgress: pct,
            eta,
            uploadMbps,
            uploadPhase: "server",
          });
        }

        // Step 3: Finalize
        try {
          await finalizeChunkedUpload({
            sessionId: chunkSessionId,
            folderName: uploadParams.folderName,
            countryCode: uploadParams.countryCode,
            metaDateTime: item.metaDateTime || null,
            WMbyComanion,
          });
        } catch (err) {
          if (err.response?.status === 409) {
            // Rare: another upload of this exact file completed while these chunks were
            // still in flight.
            const dup = err.response.data?.Result || {};
            return applyDuplicateAndFinish({ existingMediaId: dup.ExistingMediaId, existingUrl: dup.ExistingUrl });
          }
          throw err;
        }

      } else {
        // ── Small files → existing BulkUpload (single request) ──
        const singleRes = await uploadSingleFile(commonPayload, applyProgress);
        [duplicateInfo] = parseBulkUploadDuplicates(singleRes, [item.fileName]);
      }

      console.timeEnd(timerLabel);
      if (duplicateInfo.duplicate) {
        patchFileItem(sessionId, item.id, {
          status: "done",
          uploadProgress: 100,
          processedFile: null,
          originalFile: null,
          uploadPhase: "server",
          duplicate: true,
          existingMediaId: duplicateInfo.existingMediaId,
          existingUrl: duplicateInfo.existingUrl,
        });
        return "duplicate";
      }
      patchFileItem(sessionId, item.id, { status: "done", uploadProgress: 100, processedFile: null, originalFile: null, uploadPhase: "server", duplicate: false });
      return "done";
    } catch (err) {
      console.timeEnd(timerLabel);
      const msg = err.response?.data?.Message || err.message || "Upload failed";
      patchFileItem(sessionId, item.id, { status: "failed", error: msg, processedFile: null, uploadMbps: 0, uploadPhase: "server" });
      return "failed";
    }
  }, [patchFileItem]);

  /** Max images per batch request */
  const IMAGE_BATCH_SIZE = 10;

  /**
   * Upload a batch of images in a single BulkUpload request.
   * Updates progress for each item in the batch proportionally.
   */
  const uploadImageBatch = useCallback(async (sessionId, batchItems, uploadParams) => {
    const files = batchItems.map((item) => item.processedFile);
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    const batchLabel = `[Upload] Batch of ${files.length} images (${(totalSize / 1e6).toFixed(1)} MB)`;
    console.time(batchLabel);
    const uploadStart = performance.now();

    // Mark all items as uploading
    for (const item of batchItems) {
      patchFileItem(sessionId, item.id, { status: "uploading", uploadProgress: 0, eta: null, uploadMbps: 0 });
    }

    const applyBatchProgress = (evt) => {
      const total = Math.max(1, Number(evt.total || 0));
      const loaded = Math.max(0, Number(evt.loaded || 0));
      const pct = Math.max(0, Math.min(100, Math.round((loaded * 100) / total)));
      let eta = null;
      let uploadMbps = Number(evt.avgMbps || evt.speedMbps || 0);
      if (loaded > 0 && pct < 100) {
        const elapsedSec = Math.max(0.001, (performance.now() - uploadStart) / 1000);
        const speed = loaded / elapsedSec;
        const remaining = total - loaded;
        eta = Math.ceil(remaining / speed);
        if (!uploadMbps || !isFinite(uploadMbps)) {
          uploadMbps = (speed * 8) / 1_000_000;
        }
      }
      const phase = evt.phase || "server";
      for (const item of batchItems) {
        if (!navigator.onLine && pct <= 1) {
          patchFileItem(sessionId, item.id, { status: "retrying", uploadProgress: 0, eta: null, uploadMbps: 0 });
        } else {
          patchFileItem(sessionId, item.id, { status: "uploading", uploadProgress: pct, eta, uploadMbps, uploadPhase: phase });
        }
      }
    };

    const payload = {
      files,
      metaDateTimes: batchItems.map((item) => item.metaDateTime || null),
      chatRoomId: uploadParams.chatRoomId,
      eventGalleryId: uploadParams.eventGalleryId,
      galleryAlbumId: uploadParams.galleryAlbumId,
      folderName: uploadParams.folderName,
      countryCode: uploadParams.countryCode,
      isCompressed: true,
      MaxCompressed: true,
      isWatermark: uploadParams.isWatermark || false,
      WMbyComanion: batchItems.some((item) => item.watermarkDoneByCompanion),
      forceCompress: false,
    };

    try {
      let fileResults;
      try {
        const companionRes = await uploadImageBatchViaCompanion(payload, applyBatchProgress);
        fileResults = companionRes?.fileResults || null;
      } catch (companionErr) {
        console.warn("[Upload] Companion batch image upload failed. Falling back to browser bulk upload.", companionErr?.message || companionErr);
        const bulkRes = await bulkUpload(payload, applyBatchProgress);
        fileResults = parseBulkUploadDuplicates(bulkRes, batchItems.map((item) => item.fileName));
      }

      console.timeEnd(batchLabel);

      // Mark all done -- files already uploaded to this event were skipped (not
      // re-uploaded) rather than failed, so flag them instead of hiding the outcome.
      let duplicates = 0;
      batchItems.forEach((item, idx) => {
        const dup = fileResults?.[idx];
        const isDuplicate = !!dup?.duplicate;
        if (isDuplicate) duplicates += 1;
        patchFileItem(sessionId, item.id, {
          status: "done",
          uploadProgress: 100,
          processedFile: null,
          originalFile: null,
          uploadPhase: "companion",
          duplicate: isDuplicate,
          existingMediaId: dup?.existingMediaId ?? null,
          existingUrl: dup?.existingUrl ?? null,
        });
      });
      return { succeeded: batchItems.length, duplicates };
    } catch (err) {
      console.timeEnd(batchLabel);
      const msg = err.response?.data?.Message || err.message || "Upload failed";
      for (const item of batchItems) {
        patchFileItem(sessionId, item.id, { status: "failed", error: msg, processedFile: null });
      }
      return { succeeded: 0, duplicates: 0 };
    }
  }, [patchFileItem]);

  /** Start the full upload pipeline for a session */
  const startPipeline = useCallback(async (sessionId) => {
    abortRefs.current[sessionId] = false;

    // Read session from ref (synchronously available even if addSession was just called)
    const session = sessionsRef.current[sessionId];
    if (!session) return;

    // Mark as started
    updateSessions((prev) => ({
      ...prev,
      [sessionId]: { ...(prev[sessionId] || session), started: true },
    }));

    const { fileItems, chatRoomId, gallery, album, countryCode, folderName } = session;

    // ── Watermark diagnostics ────────────────────────────────────────────────
    console.group("[Pipeline] Session start");
    console.log("  gallery.Id:", gallery.Id, "| gallery.Name:", gallery.Name);
    console.log("  album.Id:", album.Id, "| album.Name:", album.Name);
    console.log("  fileItems count:", fileItems.length);
    console.groupEnd();

    // Fetch settings independently of gallery/album flags. Only enabled custom
    // watermarks are applied locally; otherwise the API supplies its fallback.
    let watermarkSettings = null;
    try {
      const wRes = await getWatermark();
      const list = wRes.data?.Result ?? wRes.data ?? [];
      const settings = Array.isArray(list) ? list : [list];
      watermarkSettings = settings.find((setting) =>
        setting?.IsEnabled === true && setting.WatermarkImageUrl?.trim()
      ) ?? null;
    } catch (err) {
      console.error("[Pipeline] Failed to fetch watermark settings; deferring to API:", err?.message, err);
    }
    const needsWatermark = watermarkSettings !== null;

    const uploadParams = {
      chatRoomId,
      eventGalleryId: gallery.Id,
      galleryAlbumId: album.Id,
      folderName,
      countryCode,
      isWatermark: needsWatermark,
      watermark: watermarkSettings,
    };
    console.log("[Pipeline] uploadParams.isWatermark:", uploadParams.isWatermark, "| uploadParams.watermark:", uploadParams.watermark ? `id=${uploadParams.watermark.Id} url=${uploadParams.watermark.WatermarkImageUrl}` : "null");

    // Resolve metadata datetimes once (in parallel) so the pipeline doesn't block
    // repeatedly on per-file metadata extraction.
    const metaDateTimeById = new Map();
    await Promise.all(
      fileItems.map(async (item) => {
        try {
          const metaDateTime = item.metaDateTime || await resolveMetaDateTime(item.originalFile, new Date());
          metaDateTimeById.set(item.id, metaDateTime);
        } catch {
          metaDateTimeById.set(item.id, item.metaDateTime || null);
        }
      })
    );

    const getMetaDateTime = (item) => item.metaDateTime || metaDateTimeById.get(item.id) || null;

    const pipelineStart = performance.now();
    let doneCount = 0;
    let failCount = 0;
    let duplicateCount = 0;

    // Pending image batch — flushed every IMAGE_BATCH_SIZE or when a video is encountered
    let imageBatch = [];

    const flushImageBatch = async () => {
      if (imageBatch.length === 0) return;
      const batch = [...imageBatch];
      imageBatch = [];
      console.log(`[Pipeline] Uploading batch of ${batch.length} images...`);
      const { succeeded, duplicates } = await uploadImageBatch(sessionId, batch, uploadParams);
      doneCount += succeeded;
      failCount += batch.length - succeeded;
      duplicateCount += duplicates;
      if (succeeded > 0) {
        setUploadVersion((v) => v + 1);
        if (session.onFileUploaded) {
          for (let i = 0; i < succeeded; i++) {
            session.onFileUploaded();
          }
        }
      }
    };

    const prepareVideoForUpload = async (videoItem) => {
      let processedFile = videoItem.processedFile;
      let wasCompressed = false;
      const metaDateTime = getMetaDateTime(videoItem);

      patchFileItem(sessionId, videoItem.id, { metaDateTime });

      // If file > 500 MB and companion is not running, fail this file immediately.
      const COMPANION_PROMPT_MB = 500;
      if (videoItem.originalFile.size > COMPANION_PROMPT_MB * 1e6) {
        const companionRunning = await checkCompanion();
        if (!companionRunning) {
          patchFileItem(sessionId, videoItem.id, {
            status: "failed",
            error: "Client app required for files over 500 MB. Go to Client App in the sidebar to install it.",
          });
          toast.error(
            <span>
              <strong>{videoItem.fileName}</strong> is over 500 MB.{" "}
              <a href="/companion" style={{ color: "#a78bfa", textDecoration: "underline" }}>Install Client App</a>{" "}
              to compress large videos.
            </span>,
            { autoClose: 10000 }
          );
          return { failed: true };
        }
      }

      patchFileItem(sessionId, videoItem.id, { status: "checking", queuePosition: 0 });

      try {
        const meta = await getVideoMeta(videoItem.originalFile);
        if (abortRefs.current[sessionId]) return { aborted: true };

        // Also compress/watermark when watermark is required but video is already low-bitrate.
        const videoNeedsWatermark = !!uploadParams.watermark;
        console.log(`[Pipeline] Video "${videoItem.fileName}" | meta.needsCompression=${meta.needsCompression} | needsWatermark=${videoNeedsWatermark} | bitrate=${meta.bitrateMbps} Mbps`);
        if (meta.needsCompression || videoNeedsWatermark) {
          patchFileItem(sessionId, videoItem.id, { status: "compressing", compressProgress: 0 });
          const compressStart = performance.now();
          let compressed;
          try {
            compressed = await compressVideo(videoItem.originalFile, {
              onProgress: (p) => {
                if (!abortRefs.current[sessionId]) {
                  patchFileItem(sessionId, videoItem.id, { compressProgress: Math.round(p * 100) });
                }
              },
              onEncoder: (enc) => {
                if (!abortRefs.current[sessionId]) {
                  patchFileItem(sessionId, videoItem.id, { compressEngine: enc });
                }
              },
              width: meta.width,
              height: meta.height,
              quality: "original",
              watermark: uploadParams.watermark || null,
            });
          } catch (compressErr) {
            console.error(`[Pipeline] ❌ compressVideo failed for "${videoItem.fileName}":`, compressErr?.message, compressErr);
            if (abortRefs.current[sessionId]) return { aborted: true };
            
            // Critical errors (insufficient disk space, etc.) MUST block the upload
            if (compressErr?.code === INSUFFICIENT_COMPANION_DISK_SPACE) {
              return {
                failed: true,
                error: compressErr.message,
              };
            }
            
            // Other compression errors: continue with uncompressed original
            return { failed: false, itemToUpload: { ...videoItem, processedFile, compressed: false, metaDateTime } };
          }
          const compressSec = ((performance.now() - compressStart) / 1000).toFixed(1);
          console.log(
            `[Pipeline] Video "${videoItem.fileName}" compress done in ${compressSec}s | watermark=${videoNeedsWatermark} | ` +
            `${(videoItem.originalFile.size / 1e6).toFixed(1)} MB -> ${(compressed.size / 1e6).toFixed(1)} MB`
          );
          if (abortRefs.current[sessionId]) return { aborted: true };
          processedFile = compressed;
          wasCompressed = compressed !== videoItem.originalFile;
          patchFileItem(sessionId, videoItem.id, {
            processedFile: compressed,
            compressed: wasCompressed,
            compressProgress: 100,
            metaDateTime,
          });
        }

      } catch (metaErr) {
        console.error(`[Pipeline] ❌ getVideoMeta failed for "${videoItem.fileName}":`, metaErr?.message);
        if (abortRefs.current[sessionId]) return { aborted: true };
      }

      return {
        failed: false,
        itemToUpload: {
          ...videoItem,
          processedFile,
          compressed: wasCompressed,
          metaDateTime,
          watermarkDoneByCompanion: wasCompressed && !!uploadParams.watermark,
        },
      };
    };

    // Process each file sequentially (compress), batch images for upload.
    // Videos are pipelined in groups: while one uploads, next one compresses.
    for (let idx = 0; idx < fileItems.length; idx++) {
      if (abortRefs.current[sessionId]) break;

      const item = fileItems[idx];
      const isVideo = item.fileType.startsWith("video/");
      const isImage = item.fileType.startsWith("image/");
      let processedFile = item.processedFile;
      let wasCompressed = false;
      const metaDateTime = getMetaDateTime(item);

      patchFileItem(sessionId, item.id, { metaDateTime });

      // Mark remaining files with their queue position
      for (let q = idx + 1; q < fileItems.length; q++) {
        patchFileItem(sessionId, fileItems[q].id, { queuePosition: q - idx });
      }

      // ── Image compression (Canvas-based, fast) ──
      // Also run through compressImage when watermark is needed — even if size/dimension
      // thresholds are not met — so PSS can apply the pixel overlay in a single pass.
      const imgNeedsCompress = needsCompression(item.originalFile);
      const imgNeedsWatermark = !!uploadParams.watermark;
      console.log(`[Pipeline] Image "${item.fileName}" | needsCompression=${imgNeedsCompress} | needsWatermark=${imgNeedsWatermark} | watermarkUrl=${uploadParams.watermark?.WatermarkImageUrl || "none"}`);
      if (isImage && (imgNeedsCompress || imgNeedsWatermark)) {
        patchFileItem(sessionId, item.id, { status: "compressing", compressProgress: 0, queuePosition: 0 });
        try {
          const compressed = await compressImage(item.originalFile, (pct) => {
            if (!abortRefs.current[sessionId]) {
              patchFileItem(sessionId, item.id, { compressProgress: pct });
            }
          }, { watermark: uploadParams.watermark || null });
          if (abortRefs.current[sessionId]) break;
          processedFile = compressed;
          console.log(`[Pipeline] Image "${item.fileName}" compress done | watermark applied=${imgNeedsWatermark} | size ${(item.originalFile.size/1024).toFixed(0)} KB → ${(compressed.size/1024).toFixed(0)} KB`);
          patchFileItem(sessionId, item.id, {
            processedFile: compressed,
            compressed: compressed !== item.originalFile,
            compressProgress: 100,
            fileSize: compressed.size,
            metaDateTime,
            watermarkDoneByCompanion: !!imgNeedsWatermark,
          });
        } catch (compressErr) {
          console.error(`[Pipeline] ❌ compressImage failed for "${item.fileName}":`, compressErr?.message, compressErr);
          if (abortRefs.current[sessionId]) break;
          // Continue with original file — upload without watermark rather than block
        }
      }

      // ── Video compression (FFmpeg WASM / Companion) ──
      if (isVideo) {
        // Flush any pending image batch before processing a video
        await flushImageBatch();
        // Build a consecutive video group from the current index.
        const videoGroup = [];
        let groupEnd = idx;
        while (groupEnd < fileItems.length && fileItems[groupEnd].fileType.startsWith("video/")) {
          videoGroup.push(fileItems[groupEnd]);
          groupEnd++;
        }

        console.log(`[Pipeline] Video pipeline: ${videoGroup.length} video(s)`);

        let nextPreparedPromise = prepareVideoForUpload(videoGroup[0]);
        for (let v = 0; v < videoGroup.length; v++) {
          if (abortRefs.current[sessionId]) break;

          const prepared = await nextPreparedPromise;
          if (abortRefs.current[sessionId] || prepared?.aborted) break;

          // Start compressing/checking the next video before uploading current.
          if (v + 1 < videoGroup.length) {
            nextPreparedPromise = prepareVideoForUpload(videoGroup[v + 1]);
          }

          if (prepared?.failed) {
            failCount++;
            if (prepared?.error) {
              patchFileItem(sessionId, videoGroup[v].id, { status: "failed", error: prepared.error });
            }
            continue;
          }

          const videoResult = await uploadOneFile(sessionId, prepared.itemToUpload, uploadParams);
          if (videoResult === "done" || videoResult === "duplicate") {
            doneCount++;
            if (videoResult === "duplicate") duplicateCount++;
            setUploadVersion((v2) => v2 + 1);
            if (session.onFileUploaded) session.onFileUploaded();
          } else if (videoResult === "failed") {
            failCount++;
          }
        }

        // Skip the videos already handled by this group.
        idx = groupEnd - 1;
        continue;
      }

      if (abortRefs.current[sessionId]) break;

      // ── Images: collect and flush in batches of 10 ──
      if (isImage) {
        const itemToUpload = {
          ...item,
          processedFile,
          compressed: processedFile !== item.originalFile,
          metaDateTime,
          watermarkDoneByCompanion: !!(uploadParams.watermark && processedFile !== item.originalFile),
        };
        imageBatch.push(itemToUpload);
        if (imageBatch.length >= IMAGE_BATCH_SIZE) {
          await flushImageBatch();
        }
        continue;
      }

      // ── Other file types: upload individually ──
      const itemToUpload = { ...item, processedFile, compressed: wasCompressed, metaDateTime };
      const result = await uploadOneFile(sessionId, itemToUpload, uploadParams);
      if (result === "done" || result === "duplicate") {
        doneCount++;
        if (result === "duplicate") duplicateCount++;
        setUploadVersion((v) => v + 1);
        if (session.onFileUploaded) session.onFileUploaded();
      } else if (result === "failed") failCount++;
    }

    // Flush remaining images in the last batch
    if (!abortRefs.current[sessionId]) {
      await flushImageBatch();
    }

    if (!abortRefs.current[sessionId]) {
      const totalSec = ((performance.now() - pipelineStart) / 1000).toFixed(1);

      // Use locally tracked counts (reliable) with ref as fallback
      const counts = { done: doneCount, failed: failCount, duplicate: duplicateCount };

      // Always bump version so listening components can refetch
      setUploadVersion((v) => v + 1);

      const duplicateNote = counts.duplicate > 0
        ? ` (${counts.duplicate} already uploaded — skipped)`
        : "";

      if (counts.failed === 0) {
        toast.success(`All ${counts.done} file${counts.done > 1 ? "s" : ""} uploaded in ${totalSec}s!${duplicateNote}`);
      } else {
        toast.error(`${counts.failed} file${counts.failed > 1 ? "s" : ""} failed. ${counts.done} succeeded${duplicateNote}. (${totalSec}s)`);
      }
      // Fire callback even on partial success so the UI refreshes
      if (counts.done > 0 && session.onUploaded) session.onUploaded();
    }
  }, [patchFileItem, uploadOneFile, uploadImageBatch, updateSessions]);

  /** Create a new upload session */
  const addSession = useCallback((session) => {
    abortRefs.current[session.id] = false;
    // Write to ref FIRST so startPipeline can read it immediately
    sessionsRef.current = { ...sessionsRef.current, [session.id]: session };
    updateSessions((prev) => ({ ...prev, [session.id]: session }));
  }, [updateSessions]);

  const cancelSession = useCallback((id) => {
    abortRefs.current[id] = true;
    removeSession(id);
    toast.warning("Upload cancelled.");
  }, [removeSession]);

  const hasActiveUploads = Object.values(sessions).some(
    (s) => s.started && s.fileItems.some((f) => ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status))
  );

  const activeSessionCount = Object.values(sessions).filter(
    (s) => s.started && s.fileItems.some((f) => ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status))
  ).length;

  return (
    <UploadContext.Provider value={{
      sessions,
      addSession,
      patchSession,
      removeSession,
      cancelSession,
      startPipeline,
      hasActiveUploads,
      activeSessionCount,
      uploadVersion,
    }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => useContext(UploadContext);
